import { Button, DatePicker, Form, Pagination, Select, message } from 'antd';
import React, { useEffect, useState } from 'react'
import styled from 'styled-components';
import Box from '../../components/Box';
import Text from '../../components/Text';
import { useDispatch } from 'react-redux';
import { setLoading } from '../../store/loading/actions';
import { setShopState } from '../../store/shop/actions';
import accountApis from '../../services/accountApis';
import { SearchOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import reportApis from '../../services/reportApis';
import { Content } from 'antd/es/layout/layout';
import { ColumnsType } from 'antd/es/table';
import { currencyFormat, formatNumber } from '../../helpers/FunctionUtils';
import { useNavigate } from 'react-router-dom';
import { TableEvenOdd } from '../account/styles';
type IFormSearchValues = {
  endDate: Dayjs;
  startDate: Dayjs;
  shopId: string;
};
const ITEMS_PER_PAGE = 10;
const FORMAT_DATE = 'MM-DD-YYYY';

interface IReportSummaryItem {
  shopId: string;
  businessName: string;
  email: string;
  transactions: number;
  incomes: number;
  creditCards: number;
  cash: number;
  giftCards: number;
  loyaltyRedeems: number;
  totalOther: number;
}

const ReportSummaryAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm<IFormSearchValues>();
  const [listSalon, setLstSalon] = useState<any[]>([]);
  const [listReportSummary, setLstReportSummary] = useState<IReportSummaryItem[]>([]);
  const [totalItems, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const getReportSummary = async (page: number, shopId: string | null, startDate: string, endDate: string) => {
    try {
      const response = await reportApis.reportSummary(shopId, startDate, endDate, page, ITEMS_PER_PAGE);
      if (response.status === 200) {
        setLstReportSummary(response.data.data.content);
        setTotal(response.data.data.totalElements);
      } else {
        message.error({ content: response.data.message, duration: 2 });
      }
    } catch (error: any) {
      message.error({ content: error.message as string, duration: 2 });
    }
  };

  const onPageChanged = async (pageNumber: number) => {
    dispatch(setLoading(true));
    try {
      setCurrentPage(pageNumber);
      const values = await form.validateFields();
      await getReportSummary(pageNumber, values.shopId, values.startDate?.format(FORMAT_DATE), values.endDate?.format(FORMAT_DATE) || '');
    } catch (error) {
      message.error({ content: 'Page has been deleted', duration: 2 });
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFinishFormSearch = async (values: IFormSearchValues) => {
    dispatch(setLoading(true));
    try {
      setCurrentPage(1);
      const values = await form.validateFields();
      await getReportSummary(1, values.shopId, values.startDate?.format(FORMAT_DATE), values.endDate?.format(FORMAT_DATE) || '');
    } catch (error) {
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleViewDetail = (id: string) => async () => {
    dispatch(setLoading(true));
    try {
      const response = await accountApis.getShopById(id);
      if (response.status === 200) {
        dispatch(setShopState(response.data.data));
        navigate(`/salon/${id}`, { state: { path: '/report' } });
      } else {
        message.error({ content: response.data.message, duration: 5 });
      }
    } catch (error) {
      message.error({ content: 'Page has been deleted', duration: 5 });
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    dispatch(setLoading(true));
    const initData = async () => {
      try {
        dispatch(setShopState(null));
        const response = await accountApis.filterSalon(null, null, 1, 10000);
        if (response.status === 200) {
          setLstSalon(response.data.data.content);
        } else {
          message.error({ content: response.data.message, duration: 2 });
        }
        const startDate = dayjs().subtract(7, 'day');
        const endDate = dayjs();
        form.setFieldsValue({ startDate, endDate });
        await getReportSummary(currentPage, null, startDate.format(FORMAT_DATE), endDate.format(FORMAT_DATE));
      } catch (error: any) {
        message.error(error.message);
      } finally {
        dispatch(setLoading(false)); // Set loading to false regardless of success or failure
      }
    };
    initData();
  }, []);

  const columns: ColumnsType<any> = [
    {
      title: <span>Business name</span>,
      dataIndex: 'businessName',
      render: (businessName: any, record: IReportSummaryItem) =>
        <Button type='link' onClick={handleViewDetail(record.shopId)}>{businessName}</Button>,
    },
    {
      title: <span>Email</span>,
      dataIndex: 'email',
      render: (email: any, record: IReportSummaryItem) =>
        <Button type='link' onClick={handleViewDetail(record.shopId)}>{email}</Button>,
    },
    {
      title: <span>Transactions</span>,
      dataIndex: 'transactions',
      render: (transactions: any) => <span>{formatNumber(transactions)}</span>,
    },
    {
      title: <span>Incomes</span>,
      dataIndex: 'incomes',
      render: (incomes: any) => <span>{currencyFormat(incomes, 'USD')}</span>,
    },
    {
      title: <span>Credit cards</span>,
      dataIndex: 'creditCards',
      render: (creditCards: any) => <span> {currencyFormat(creditCards, 'USD')}</span>,
    },
    {
      title: <span>Cash</span>,
      dataIndex: 'cash',
      render: (cash: any) => <span> {currencyFormat(cash, 'USD')}</span>,
    },
    {
      title: <span>Gift cards</span>,
      dataIndex: 'giftCards',
      render: (giftCards: any) => <span> {currencyFormat(giftCards, 'USD')}</span>,
    },
    {
      title: <span>Loyalty redeems</span>,
      dataIndex: 'loyaltyRedeems',
      render: (loyaltyRedeems: any) => <span> {currencyFormat(loyaltyRedeems, 'USD')}</span>,
    },
    {
      title: <span>Others</span>,
      dataIndex: 'totalOther',
      render: (totalOther: any) => <span> {currencyFormat(totalOther, 'USD')}</span>,
    },
  ];

  return (
    <PageStyled>
      <Box className="d-between" alignItems='center'>
        <Text variant="H5">Report ({totalItems || 0})</Text>
        <FormStyled form={form} name="search"
          // @ts-ignore
          onFinish={handleFinishFormSearch}>
          <Form.Item
            name="startDate">
            <DatePicker
              style={{ width: '100%' }}
              size="large"
              format="MM-DD-YYYY"
              showTime={false}
              inputReadOnly={true} />
          </Form.Item>
          <Form.Item
            name="endDate">
            <DatePicker
              style={{ width: '100%' }}
              size="large"
              format="MM-DD-YYYY"
              showTime={false}
              inputReadOnly={true}
            />
          </Form.Item>
          <Form.Item
            name="shopId">
            <Select size='large' defaultValue={''} style={{ width: '100%' }} showSearch optionFilterProp="label">
              <Select.Option value={''} label={'All Salon - Zipcode'}>
                All Salon - Zipcode
              </Select.Option>
              {listSalon.map((item: any) => (
                <Select.Option key={item.shopId} value={item.shopId} label={`${item.shopName} - ${item.zipCode}`}>
                  {item.shopName} - {item.zipCode}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Button htmlType='submit' type="primary" style={{ width: '100%' }} icon={<SearchOutlined />} size='large'>
            Search
          </Button>
        </FormStyled>
      </Box>
      <TableContentStyled
        columns={columns}
        dataSource={listReportSummary}
        rowKey="email"
        bordered
        pagination={false} />

      <PagingStyled>
        <Pagination
          current={currentPage}
          total={totalItems}
          pageSize={ITEMS_PER_PAGE}
          onChange={onPageChanged}
          showSizeChanger={false}
        />
      </PagingStyled>
    </PageStyled>
  )
}

export default ReportSummaryAdmin;

const PageStyled = styled.div`
  padding: 1.5rem;
`;
const FormStyled = styled(Form)`
  display: flex;
  gap: 16px;
  .ant-form-item  {
    margin-bottom: 0;
    min-width: 15vw;
  }
`;
const TableContentStyled = styled(TableEvenOdd)`
margin-top: 16px;
    && .status {
        text-align: center;
    }
`;

const PagingStyled = styled(Content)`
    text-align: right;
    margin-top: 10px;
`;

// const buttonLink = styled.div`
//     color: 
//     &:hover {
//       cursor: pointer;
//     }
// `;